import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Add, ExpandMore, SpaceDashboardOutlined } from "@mui/icons-material"
import { Box, Divider, IconButton, Skeleton, Tabs, Tooltip, Typography, type Theme } from "@mui/material"
import { Creators as DataCreators } from "model/data"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { OK, ERROR } from "helpers/data-statuses"
import { useAppDispatch } from "hooks/redux"
import { useAccountAlias, useIsMultiDashboardsEnabled } from "resources/account"
import {
  useDashboardInstances,
  useCreateInstance,
  useSaveInstanceWorkspace,
  DASHBOARD_INSTANCES_RESOURCE,
  type DashboardInstance,
} from "resources/dashboard-instances"
import { useCreateLayout } from "resources/layouts"
import { useDashboardId } from "../../context/dashboard-id"
import {
  instantiateInstanceFromLayout,
  buildDefaultInstanceConfig,
  NEW_DASHBOARD_NAME,
} from "../../layouts/instantiate-instance-from-layout"
import DashboardTab from "./dashboard-tab"
import SwitcherOverflowMenu from "./switcher-overflow-menu"

// Stable empty fallback: a fresh [] each render would re-run the sort memo and churn the Tabs.
const EMPTY_INSTANCES: DashboardInstance[] = []

// Segmented-control "Apple" tabs: action.hover track + raised pill behind the active tab.
const appleTabsSx = (theme: Theme) => ({
  minHeight: "auto",
  flex: "0 1 auto",
  maxWidth: "100%",
  minWidth: 0,
  alignSelf: "center",
  borderRadius: "10px",
  p: 0.5,
  bgcolor: "action.hover",
  "& .MuiTabs-flexContainer": { gap: 0.5 },
  "& .MuiTabs-indicator": {
    height: "100%",
    borderRadius: "8px",
    zIndex: 0,
    bgcolor: "background.paper",
    boxShadow: theme.shadows[1],
    transition: theme.transitions.create(["left", "width"], {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeInOut,
    }),
  },
  "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
})

// Most-recent first; tiebreak by instanceId asc for a stable order. Nulls (never opened) sort last.
function sortByRecency(instances: DashboardInstance[]): DashboardInstance[] {
  return [...instances].sort((a, b) => {
    const left = a.lastOpenedAt
    const right = b.lastOpenedAt
    if (left !== right) {
      if (left == null) return 1
      if (right == null) return -1
      return left < right ? 1 : -1
    }
    return a.instanceId < b.instanceId ? -1 : 1
  })
}

export default function DashboardSwitcher() {
  const isEnabled = useIsMultiDashboardsEnabled()
  const [instances, status] = useDashboardInstances(isEnabled)
  const currentId = useDashboardId()
  const accountAlias = useAccountAlias()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const createLayout = useCreateLayout()
  const createInstance = useCreateInstance()
  const saveWorkspace = useSaveInstanceWorkspace()

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  // Fires the load-failure toast at most once per mount; a boolean ref (not state) avoids a
  // re-render and keeps the dispatch in the effect, not the render body.
  const erroredToastedRef = useRef(false)

  const sortedInstances = useMemo(
    () => sortByRecency(instances ?? EMPTY_INSTANCES),
    [instances],
  )

  // A resource gate must accept ERROR as terminal too, or the bar hangs on the skeleton forever
  // when the fetch errors. On ERROR we render just the "+ New" affordance (and toast once).
  const isLoading = !(status === OK || status === ERROR)

  useEffect(() => {
    if (status !== ERROR || erroredToastedRef.current) return
    erroredToastedRef.current = true
    dispatch(NotificationCreators.add("error", null, "Could not load your dashboards."))
  }, [status, dispatch])

  const handleSelect = useCallback(
    (slug: string) => {
      if (!accountAlias) return
      navigate(`/${accountAlias}/dashboard/${slug}`)
    },
    [accountAlias, navigate],
  )

  const closeMenu = useCallback(() => { setMenuAnchor(null) }, [])

  const handleNewDashboard = useCallback(() => {
    if (isCreating) return
    setIsCreating(true)
    const { config, workspace } = buildDefaultInstanceConfig()
    instantiateInstanceFromLayout({
      name: NEW_DASHBOARD_NAME,
      config,
      workspace,
      createLayout,
      createInstance,
      saveWorkspace,
    })
      .then(({ slug }) => {
        // The cached list predates this create, so reset it to force a refetch before/while we
        // route to the new slug — otherwise the route resolver treats the new id as unknown.
        dispatch(DataCreators.resetCollections([DASHBOARD_INSTANCES_RESOURCE]))
        if (accountAlias) navigate(`/${accountAlias}/dashboard/${slug}`)
      })
      .catch(() => {
        dispatch(NotificationCreators.add("error", null, "Could not create a new dashboard."))
      })
      .finally(() => { setIsCreating(false) })
  }, [isCreating, createLayout, createInstance, saveWorkspace, dispatch, accountAlias, navigate])

  if (!isEnabled) {
    // Flag off: the original single-dashboard chrome — a dashboard glyph + "Dashboard" label. The
    // switcher tab strip only appears once multi-dashboard is enabled.
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0, alignSelf: "stretch" }}>
        <SpaceDashboardOutlined sx={{ fontSize: 30, color: "common.onBackground" }} />
        <Typography sx={{ ml: 0.5, fontSize: "18px", lineHeight: 1, color: "common.onBackground", whiteSpace: "nowrap" }}>
          Dashboard
        </Typography>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0, alignSelf: "stretch" }}>
        <Skeleton variant="rounded" width={132} height={26} />
        <Skeleton variant="rounded" width={132} height={26} />
      </Box>
    )
  }

  // Match the active tab on instanceId or slug (URL may carry either); false on the bare /dashboard
  // landing avoids MUI's out-of-range warning.
  const activeInstance = sortedInstances.find(
    (instance) => instance.instanceId === currentId || instance.slug === currentId,
  )
  const activeValue = activeInstance ? activeInstance.slug : false

  return (
    // flex:1 fills where the icon/title used to be; minWidth:0 lets the scrollable strip shrink.
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0, alignSelf: "stretch" }}>
      {sortedInstances.length > 0 && (
        <Tabs
          value={activeValue}
          variant="scrollable"
          scrollButtons="auto"
          sx={appleTabsSx}
        >
          {sortedInstances.map((instance) => (
            <DashboardTab
              key={instance.instanceId}
              value={instance.slug}
              name={instance.name}
              onSelect={handleSelect}
            />
          ))}
        </Tabs>
      )}
      {/* Divider only with tabs present, else it's a stray leading divider in the empty state. */}
      {sortedInstances.length > 0 && (
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
      )}
      <Tooltip title="More dashboards">
        <IconButton
          aria-label="More dashboards"
          size="small"
          onClick={(event) => { setMenuAnchor(event.currentTarget) }}
        >
          <ExpandMore />
        </IconButton>
      </Tooltip>
      <Tooltip title="New dashboard">
        <span>
          <IconButton
            aria-label="New dashboard"
            size="small"
            disabled={isCreating}
            onClick={handleNewDashboard}
          >
            <Add />
          </IconButton>
        </span>
      </Tooltip>
      <SwitcherOverflowMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        instances={sortedInstances}
        currentId={currentId}
        onSelect={handleSelect}
        onNewDashboard={handleNewDashboard}
        isCreating={isCreating}
      />
    </Box>
  )
}
